// src/pages/TenderDetailPage.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import TenderFormModal from '../components/TenderFormModal';
import {
  TENDER_STATUSES, TENDER_SECTORS, TENDER_SOURCES, PRIORITIES,
  formatCurrency, formatDate, getLabelFor, daysUntilColor
} from '../utils/constants';

export default function TenderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isNew = id === 'new';

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    api.get(`/tenders/${id}`).then(setTender).finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    if (isNew) setShowEdit(true);
  }, [isNew]);

  const handleUpdated = (updated) => {
    if (isNew) { navigate(`/tenders/${updated.id}`); return; }
    setTender(updated);
    setShowEdit(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this tender? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.delete(`/tenders/${id}`);
      navigate('/tenders');
    } catch (err) {
      alert(err.message);
      setDeleting(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const updated = await api.put(`/tenders/${id}`, { status: newStatus });
      setTender(t => ({ ...t, ...updated }));
    } catch (e) {
      alert(e.message);
    }
    setUpdatingStatus(false);
  };

  if (isNew) return showEdit ? <TenderFormModal onClose={() => navigate('/tenders')} onSaved={handleUpdated} /> : null;
  if (loading) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Loading…</div>;
  if (!tender) return <div style={{ padding: '2rem', color: 'var(--red)' }}>Tender not found.</div>;

  const days = tender.days_until_close !== null ? parseInt(tender.days_until_close) : null;
  const rawStatus = tender.tender_status_raw || '';
  const rawStatusColor = rawStatus === 'OPEN' ? 'var(--green)'
    : rawStatus === 'AWARDED' ? 'var(--blue)'
    : rawStatus === 'CANCELLED' ? 'var(--red)'
    : 'var(--text-muted)';

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
        <Link to="/tenders" style={{ color: 'var(--text-muted)' }}>Tenders</Link> / {tender.title}
      </div>

      {/* Header */}
      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            {rawStatus && (
              <span style={{
                fontSize: '0.75rem', fontWeight: 700, color: rawStatusColor,
                background: rawStatusColor + '22', padding: '0.2rem 0.6rem',
                borderRadius: 10, textTransform: 'uppercase',
              }}>
                {rawStatus}
              </span>
            )}
            {tender.tender_type && (
              <span style={{
                fontSize: '0.75rem', color: 'var(--text-secondary)',
                background: 'var(--bg-hover)', padding: '0.2rem 0.6rem', borderRadius: 10,
              }}>
                {tender.tender_type}
              </span>
            )}
            <span className={`badge priority-${tender.priority}`}>
              {getLabelFor(PRIORITIES, tender.priority)}
            </span>
            {days !== null && rawStatus === 'OPEN' && (
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: daysUntilColor(days) }}>
                {days < 0 ? `Closed ${Math.abs(days)}d ago` : days === 0 ? 'Closes today' : `Closes in ${days} days`}
              </span>
            )}
            {tender.source_name && (
              <span style={{
                fontSize: '0.72rem', background: 'var(--bg-hover)',
                color: 'var(--text-secondary)', padding: '0.15rem 0.5rem', borderRadius: 10,
              }}>
                {tender.source_name}
              </span>
            )}
          </div>
          <div className="page-title" style={{ fontSize: '1.4rem', lineHeight: 1.3 }}>{tender.title}</div>
          {(tender.client_name_db || tender.client_name) && (
            <div style={{ color: 'var(--text-secondary)', marginTop: '0.3rem', fontSize: '0.95rem' }}>
              {tender.client_name_db || tender.client_name}
            </div>
          )}
          {tender.reference_number && (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontFamily: 'monospace', marginTop: '0.2rem' }}>
              Ref: {tender.reference_number}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          {tender.source_url && (
            <a href={tender.source_url} target="_blank" rel="noreferrer" className="btn btn-secondary">
              View Source ↗
            </a>
          )}
          {tender.document_url && (
            <a href={tender.document_url} target="_blank" rel="noreferrer" className="btn btn-secondary">
              Documents ↗
            </a>
          )}
          <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>Edit</button>
          {user?.role === 'admin' && (
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.25rem' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* Description */}
          {tender.description && (
            <div className="card">
              <SectionTitle>Description</SectionTitle>
              <div style={{
                fontSize: '0.875rem', color: 'var(--text-secondary)',
                lineHeight: 1.7, whiteSpace: 'pre-wrap',
              }}>
                {tender.description}
              </div>
            </div>
          )}

          {/* Key Details */}
          <div className="card">
            <SectionTitle>Details</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Field label="Reference #"    value={tender.reference_number} mono />
              <Field label="Tender Type"    value={tender.tender_type} />
              <Field label="Sector"         value={getLabelFor(TENDER_SECTORS, tender.sector)} />
              <Field label="Source"         value={getLabelFor(TENDER_SOURCES, tender.source)} />
              <Field label="Estimated Value" value={
                <span style={{ color: 'var(--accent)', fontWeight: 700 }}>
                  {formatCurrency(tender.estimated_value, tender.currency)}
                </span>
              } />
              <Field label="Location"       value={[tender.province, tender.country].filter(Boolean).join(', ')} />
              <Field label="Published"      value={formatDate(tender.published_date || tender.issue_date)} />
              <Field label="Closing Date"   value={<span style={{ color: days !== null && days <= 7 ? 'var(--red)' : 'inherit' }}>{formatDate(tender.closing_date)}</span>} />
              <Field label="Decision Date"  value={formatDate(tender.decision_date)} />
              {tender.submission_date && <Field label="Submitted" value={formatDate(tender.submission_date)} />}
              <Field label="Assigned To"    value={tender.assigned_name} />
              <Field label="Created By"     value={tender.created_by_name} />
            </div>

            {/* Awarded info */}
            {(tender.awarded_to || tender.awarded_amount) && (
              <div style={{
                marginTop: '1rem', padding: '0.75rem 1rem',
                background: 'var(--blue-dim)', border: '1px solid var(--blue)',
                borderRadius: 'var(--radius-sm)',
              }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '0.4rem' }}>
                  Contract Awarded
                </div>
                <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                  {tender.awarded_to && (
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Awarded To</div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{tender.awarded_to}</div>
                    </div>
                  )}
                  {tender.awarded_amount && (
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--accent)' }}>{formatCurrency(tender.awarded_amount)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tender.go_nogo_reason && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bg-base)', borderRadius: 6, borderLeft: '3px solid var(--border-light)' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>Go/No-Go Reason</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{tender.go_nogo_reason}</div>
              </div>
            )}

            {tender.notes && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bg-base)', borderRadius: 6 }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>Notes</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{tender.notes}</div>
              </div>
            )}
          </div>

          {/* Activity Log */}
          <div className="card">
            <SectionTitle>Activity Log</SectionTitle>
            {(!tender.activity || tender.activity.length === 0) ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No activity yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {tender.activity.map((a, i) => (
                  <div key={a.id} style={{
                    display: 'flex', gap: '0.75rem',
                    paddingBottom: '0.75rem', marginBottom: '0.75rem',
                    borderBottom: i < tender.activity.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--border-light)', marginTop: 6, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '0.82rem' }}>
                        <span style={{ fontWeight: 600 }}>{a.user_name || 'System'}</span>{' '}
                        <span style={{ color: 'var(--text-muted)' }}>
                          {a.action === 'created' && 'created this tender'}
                          {a.action === 'status_change' && (
                            <>changed status → <span style={{ color: 'var(--accent)' }}>{getLabelFor(TENDER_STATUSES, a.new_value)}</span></>
                          )}
                          {a.action === 'updated' && 'updated this tender'}
                        </span>
                        {a.notes && <span style={{ color: 'var(--text-muted)' }}> — {a.notes}</span>}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                        {new Date(a.created_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* Links */}
          <div className="card">
            <SectionTitle>Links</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {tender.source_url && (
                <a href={tender.source_url} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ justifyContent: 'center' }}>
                  View on {tender.source_name || 'Source'} ↗
                </a>
              )}
              {tender.document_url && (
                <a href={tender.document_url} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ justifyContent: 'center' }}>
                  Download Documents ↗
                </a>
              )}
              {!tender.source_url && !tender.document_url && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No links available.</div>
              )}
            </div>
          </div>

          {/* Pipeline status */}
          <div className="card">
            <SectionTitle>Our Pipeline Status</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {TENDER_STATUSES.map(s => {
                const isActive = s.value === tender.status;
                return (
                  <button
                    key={s.value}
                    disabled={isActive || updatingStatus}
                    onClick={() => updateStatus(s.value)}
                    style={{
                      padding: '0.4rem 0.75rem',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid',
                      borderColor: isActive ? 'var(--accent)' : 'var(--border)',
                      background: isActive ? 'var(--accent-dim)' : 'transparent',
                      color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
                      fontSize: '0.82rem', textAlign: 'left',
                      cursor: isActive ? 'default' : 'pointer',
                      fontWeight: isActive ? 700 : 400,
                      transition: 'all 0.1s',
                    }}
                  >
                    {isActive ? '● ' : '○ '}{s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Metadata */}
          <div className="card">
            <SectionTitle>Metadata</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <MetaRow label="Source" value={tender.source_name || getLabelFor(TENDER_SOURCES, tender.source)} />
              <MetaRow label="Sector" value={getLabelFor(TENDER_SECTORS, tender.sector)} />
              <MetaRow label="Province" value={tender.province} />
              <MetaRow label="Added" value={new Date(tender.created_at).toLocaleDateString('en-CA', { dateStyle: 'medium' })} />
            </div>
          </div>
        </div>
      </div>

      {showEdit && (
        <TenderFormModal existing={tender} onClose={() => setShowEdit(false)} onSaved={handleUpdated} />
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.85rem',
      letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase',
      marginBottom: '0.75rem',
    }}>
      {children}
    </div>
  );
}

function Field({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.15rem' }}>{label}</div>
      <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
    </div>
  );
}

function MetaRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
